/*
 * Doverfield Home: product page behaviour (loaded by sections/main-product).
 *
 * Custom elements
 *   <product-gallery>  scroll-snap gallery with prev/next, a counter and
 *                      thumbnails (desktop). goTo(index) scrolls to a slide.
 *   <variant-picker>   option radios. Only listed variants exist (Shopify
 *                      rule), so for each option after the first it shows
 *                      only the values that exist with the options chosen
 *                      before it. Dispatches `variant:change` with
 *                      detail.variant.
 *   <product-form>     owns the Shopify product form ({% form 'product' %}):
 *                      the picker, the quantity and the Add to cart button.
 *                      Updates price, compare-at, "Save $x", availability,
 *                      the gallery and ?variant= on change. On submit it adds
 *                      to the cart through window.DoverfieldCart (Cart AJAX
 *                      API) and then dispatches `cart:open` so the drawer
 *                      opens. Without script the form posts to /cart/add.
 *   <sticky-atc>       the mobile bar: "Choose your pack" before the picker,
 *                      Add to cart once the main button has scrolled past,
 *                      hidden while either is on screen.
 *   <product-video>    the gallery's muted clip; plays while on screen.
 *
 * Variant data comes from <script type="application/json" data-product-json>
 * in the same section.
 */

import { prefersReducedMotion } from './global.js';

const defineElement = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const moneyWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const formatMoney = (cents) => money.format((Number(cents) || 0) / 100);
const formatMoneyShort = (cents) => {
  const c = Math.round(Number(cents) || 0);
  return c % 100 === 0 ? moneyWhole.format(c / 100) : money.format(c / 100);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function readProductData(root) {
  const el = root.querySelector('[data-product-json]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || 'null');
  } catch {
    return null;
  }
}

/** "Set of 3" -> 3; a single (or any other pack value) -> 0. */
const packCount = (value) => {
  const match = /^Set of\s+(\d+)/.exec(String(value || ''));
  return match ? Number(match[1]) : 0;
};

/** The variant's option values, trimmed to the product's option count. */
function variantOptions(variant, count) {
  const values = Array.isArray(variant.options) && variant.options.length
    ? variant.options
    : [variant.option1, variant.option2, variant.option3];
  return values.slice(0, count);
}

/* ------------------------------------------------------------------------ */
/* Gallery                                                                   */
/* ------------------------------------------------------------------------ */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbList = this.querySelector('[data-gallery-thumbs]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.index = 0;
    if (!this.track || this.slides.length === 0) return;

    this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
    this.next?.addEventListener('click', () => this.goTo(this.index + 1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
    });

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }
    });

    // Track which slide is mostly in view, whether it got there by swipe,
    // trackpad, arrow buttons or thumbnails.
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.setActive(Number(entry.target.dataset.index));
        }
      });
    }, { root: this.track, threshold: [0.6] });
    this.slides.forEach((slide) => this.observer.observe(slide));
    this.setActive(0);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  goTo(index, { instant = false } = {}) {
    const count = this.slides.length;
    if (!count) return;
    const target = Math.max(0, Math.min(count - 1, index));
    const slide = this.slides[target];
    // 'instant' (not 'auto') so the stylesheet's scroll-behavior: smooth does not apply.
    const behavior = instant || prefersReducedMotion() ? 'instant' : 'smooth';
    // The track's inline padding (the page gutter on phones) is the snap inset.
    const inset = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft - inset, behavior });
    this.setActive(target);
  }

  setActive(index) {
    if (!Number.isFinite(index)) return;
    this.index = index;
    const count = this.slides.length;
    if (this.counter) {
      const template = this.dataset.counterTemplate || '[index] / [count]';
      this.counter.textContent = template.replace('[index]', String(index + 1)).replace('[count]', String(count));
    }
    if (this.prev) {
      this.prev.disabled = index <= 0;
      this.prev.setAttribute('aria-controls', this.slides[Math.max(0, index - 1)].id || '');
    }
    if (this.next) {
      this.next.disabled = index >= count - 1;
      this.next.setAttribute('aria-controls', this.slides[Math.min(count - 1, index + 1)].id || '');
    }
    this.slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== index && this.isOneUp()));
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.scrollThumbIntoView(index);
  }

  // Every layout shows one slide at a time; inert keeps focus off hidden slides.
  isOneUp() {
    return true;
  }

  scrollThumbIntoView(index) {
    const thumb = this.thumbs[index];
    const list = this.thumbList;
    if (!thumb || !list || list.offsetParent === null) return;
    const item = thumb.parentElement;
    const vertical = list.scrollHeight > list.clientHeight + 1;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    if (vertical) {
      const top = item.offsetTop - list.offsetTop;
      if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTo({ top: top - (list.clientHeight - item.offsetHeight) / 2, behavior });
      }
    } else {
      const left = item.offsetLeft - list.offsetLeft;
      if (left < list.scrollLeft || left + item.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollTo({ left: left - (list.clientWidth - item.offsetWidth) / 2, behavior });
      }
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Variant picker                                                            */
/* ------------------------------------------------------------------------ */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('fieldset[data-option-index]'));
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-option-input]');
      if (!input) return;
      const group = input.closest('fieldset[data-option-index]');
      this.onOptionChange(Number(group.dataset.optionIndex));
    });
  }

  /** Called by <product-form> once it has parsed the product data. */
  init(product, initialVariant) {
    this.product = product;
    this.optionCount = Math.max(1, (product.options || []).length);
    this.variants = (product.variants || []).map((variant) => ({
      ...variant,
      optionValues: variantOptions(variant, this.optionCount),
    }));
    const initial = initialVariant && this.variants.find((v) => String(v.id) === String(initialVariant.id));
    if (initial) this.applyVariant(initial, { emit: false });
  }

  selectedValues() {
    return this.groups.map((group) => group.querySelector('[data-option-input]:checked')?.value ?? null);
  }

  /** Variants whose first `depth` option values match `values`. */
  matching(values, depth) {
    return this.variants.filter((variant) => {
      for (let i = 0; i < depth; i += 1) {
        if (variant.optionValues[i] !== values[i]) return false;
      }
      return true;
    });
  }

  onOptionChange(changedIndex) {
    const values = this.selectedValues();
    // Keep the shopper's later choices when that exact combination exists
    // and is in stock; otherwise move to the in-stock variant for the
    // options chosen so far that keeps the most of them (a colour carries
    // over to a size that has it), falling back to the first that exists.
    const exact = this.variants.find((v) => v.optionValues.every((value, i) => value === values[i]));
    let variant = exact && exact.available ? exact : null;
    if (!variant) {
      const candidates = this.matching(values, changedIndex + 1);
      const kept = (v) => v.optionValues.filter((value, i) => i > changedIndex && value === values[i]).length;
      let best = null;
      for (const v of candidates) {
        if (v.available && (!best || kept(v) > kept(best))) best = v;
      }
      variant = best || exact || candidates[0] || null;
    }
    this.applyVariant(variant, { emit: true });
  }

  applyVariant(variant, { emit }) {
    if (variant) {
      this.groups.forEach((group, i) => {
        const value = variant.optionValues[i];
        group.querySelectorAll('[data-option-input]').forEach((input) => {
          input.checked = input.value === value;
        });
      });
    }
    this.refreshValues();
    if (emit) {
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }

  // Show only values that exist for the options chosen before them, mark
  // values whose variants are all sold out, and update the legends.
  refreshValues() {
    const values = this.selectedValues();
    this.groups.forEach((group, index) => {
      const candidates = this.matching(values, index);
      group.querySelectorAll('[data-option-input]').forEach((input) => {
        const withValue = candidates.filter((v) => v.optionValues[index] === input.value);
        const wrap = input.closest('[data-option-value-wrap]');
        if (wrap) wrap.hidden = withValue.length === 0;
        const available = withValue.some((v) => v.available);
        const label = input.nextElementSibling;
        label?.classList.toggle('is-unavailable', !available);
        const suffix = label?.querySelector('[data-sold-out-suffix]');
        if (suffix) suffix.hidden = available;
      });
      const selected = group.querySelector('[data-selected-value]');
      if (selected) selected.textContent = values[index] ?? '';
    });
  }
}

/* ------------------------------------------------------------------------ */
/* Product form                                                              */
/* ------------------------------------------------------------------------ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('section') || document;
    this.product = readProductData(this.section);
    this.strings = (this.product && this.product.strings) || {};
    this.idInput = this.querySelector('[data-variant-id-input]');
    this.button = this.querySelector('[data-add-to-cart]');
    this.buttonLabel = this.querySelector('[data-add-to-cart-label]');
    this.quantityInput = this.querySelector('quantity-input input');
    this.error = this.querySelector('[data-form-error]');
    this.errorText = this.querySelector('[data-form-error-text]');
    this.live = this.querySelector('[data-form-live]');
    this.availability = this.querySelector('[data-availability]');
    this.availabilityText = this.querySelector('[data-availability-text]');
    this.picker = this.querySelector('variant-picker');
    this.gallery = this.section.querySelector('product-gallery');
    this.sticky = this.section.querySelector('sticky-atc');

    if (!this.product) return;
    const variants = this.product.variants || [];
    this.variant = variants.find((v) => String(v.id) === String(this.idInput?.value)) || variants[0] || null;

    // ?variant= in the URL wins, as on Shopify, except a gift-only add-on
    // variant (data-gift-only-variant), which the page never selects.
    const requested = new URLSearchParams(window.location.search).get('variant');
    const fromUrl = requested && requested !== this.dataset.giftOnlyVariant
      ? variants.find((v) => String(v.id) === requested) : null;
    if (fromUrl) this.variant = fromUrl;

    const init = () => {
      if (this.picker && typeof this.picker.init === 'function') this.picker.init(this.product, this.variant);
      if (fromUrl) this.render(this.variant, { updateUrl: false, instantGallery: true });
    };
    // Child custom elements may upgrade after this one.
    if (customElements.get('variant-picker')) init();
    else customElements.whenDefined('variant-picker').then(init);

    // Optional add-on (custom.addon_variant or custom.addon): a ticked box
    // adds its price to the button total and one to the cart with the item.
    // While it's free, [data-addon-gift] adds it as its own Free gift line.
    this.addon = this.querySelector('[data-addon]');
    this.gift = this.querySelector('[data-addon-gift]');
    this.addon?.addEventListener('change', () => this.renderButton(this.variant));

    // A timed offer (the free pump for the weekend) ends with its countdown:
    // the card, the free row and the free line go, and a box, if any, offers
    // the add-on on its own.
    // On a product in the sale itself the countdown sits on the price sign.
    this.offerCard = document.querySelector(`[data-offer-timed="${this.dataset.section}"]`);
    const offerClock = this.offerCard?.querySelector('promo-countdown')
      || (this.offerCard && this.section?.querySelector('[data-product-price] promo-countdown'));
    if (offerClock) {
      if (offerClock.dataset.phase === 'ended') this.endOffer();
      else offerClock.addEventListener('promo:ended', () => this.endOffer());
    }

    this.addEventListener('variant:change', (event) => {
      this.render(event.detail.variant, { updateUrl: true });
    });

    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit(this.button);
    });
  }

  /**
   * Market Hall price sign: the pack name, the same bags bought singly
   * crossed out, the price and the price per bag under it, for products with a "Pack" option (pack_index in the
   * product JSON). Same arithmetic as main-product.liquid: a "Set of N" costs
   * its price / N per bag; under a single the sign names the lowest per-bag
   * price of the sets that save on singles.
   */
  renderSign(variant) {
    const index = Number(this.product.pack_index);
    const nameEl = this.section.querySelector('[data-sign-name]');
    const noteEl = this.section.querySelector('[data-sign-note]');
    if (!variant || !(index >= 0)) return;
    const key = `option${index + 1}`;
    const variants = this.product.variants || [];
    const singles = variants.filter((v) => !packCount(v[key])).map((v) => v.price);
    const unit = singles.length ? Math.min(...singles) : null;
    const pack = variant[key] || '';
    let note = '';
    if (unit !== null) {
      const count = packCount(pack);
      if (count) {
        note = (this.strings.perBag || '').split('[each]').join(formatMoneyShort(Math.floor(variant.price / count)));
      } else {
        let best = null;
        for (const v of variants) {
          const n = packCount(v[key]);
          if (n && unit * n - v.price > 0 && (!best || n > best.n)) best = { n, price: v.price };
        }
        if (best) note = (this.strings.setsFrom || '').split('[each]').join(formatMoneyShort(Math.floor(best.price / best.n)));
      }
    }
    // Crossed-out price: the same bags bought singly (a real store price).
    const wasEl = this.section.querySelector('[data-sign-was]');
    const wasAmount = this.section.querySelector('[data-sign-was-amount]');
    if (wasEl && wasAmount) {
      const count = packCount(pack);
      const full = Number(variant.compare_at_price) > variant.price ? 0 : (unit !== null && count ? unit * count : 0);
      const show = full > variant.price;
      wasAmount.textContent = show ? formatMoney(full) : '';
      wasEl.hidden = !show;
      const saveEl = this.section.querySelector('[data-sign-save]');
      if (saveEl) {
        const percent = show ? Math.floor(((full - variant.price) * 100) / full) : 0;
        saveEl.textContent = show ? (this.strings.savePercent || '').split('[percent]').join(String(percent)) : '';
      }
    }
    if (nameEl) {
      nameEl.textContent = pack;
      nameEl.hidden = !pack;
    }
    if (noteEl) {
      noteEl.textContent = note;
      noteEl.hidden = !note;
    }

    // A set's colour legend reads "Colors in this set" and the quantity
    // counts sets.
    const isSet = packCount(pack) > 0;
    const legend = this.section.querySelector('[data-color-legend]');
    if (legend) legend.textContent = isSet ? legend.dataset.set : legend.dataset.single;
    const qtyLabel = this.section.querySelector('[data-qty-label]');
    if (qtyLabel) qtyLabel.textContent = isSet ? qtyLabel.dataset.set : qtyLabel.dataset.single;
    if (qtyLabel && this.quantityInput) {
      const aria = isSet ? this.strings.quantitySetsAria : this.strings.quantityAria;
      if (aria) this.quantityInput.setAttribute('aria-label', aria);
    }
  }

  /**
   * Black Friday (product.promo): the everyday price that starts after the
   * deal ("$60 from Dec 2"), from product.everyday_prices. A future price
   * the store will charge, never shown as a former price.
   */
  renderPromo(variant) {
    const index = Number(this.product.pack_index);
    if (!this.product.promo || !variant || !(index >= 0)) return;
    const pack = variant[`option${index + 1}`] || '';
    const after = this.section.querySelector('[data-sign-after]');
    if (after) {
      const everyday = Number((this.product.everyday_prices || {})[pack]) || 0;
      const show = everyday > variant.price;
      after.textContent = show ? (this.strings.everyday || '').split('[sale]').join(formatMoneyShort(variant.price)).split('[price]').join(formatMoneyShort(everyday)) : '';
      after.hidden = !show;
    }
  }

  render(variant, { updateUrl = false, instantGallery = false } = {}) {
    this.variant = variant;
    this.hideError();
    const available = Boolean(variant && variant.available);
    if (this.idInput) this.idInput.value = variant ? variant.id : '';

    // Price, compare-at and "Save $x" swap instantly (design-system 6.1).
    const priceRoot = this.section.querySelector('[data-product-price] .price');
    if (priceRoot && variant) {
      const compare = Number(variant.compare_at_price) || 0;
      const onSale = compare > variant.price;
      priceRoot.classList.toggle('price--on-sale', onSale);
      const current = priceRoot.querySelector('[data-price-current]');
      if (current) current.textContent = formatMoney(variant.price);
      const compareWrap = priceRoot.querySelector('[data-price-compare-wrap]');
      const compareEl = priceRoot.querySelector('[data-price-compare]');
      if (compareWrap) compareWrap.hidden = !onSale;
      if (compareEl) compareEl.textContent = onSale ? formatMoney(compare) : '';
      const save = priceRoot.querySelector('[data-price-save]');
      if (save) {
        save.hidden = !onSale;
        save.textContent = onSale ? (this.strings.save || 'Save [amount]').replace('[amount]', formatMoneyShort(compare - variant.price)) : '';
      }
      const label = priceRoot.querySelector('.visually-hidden');
      if (label) label.textContent = onSale ? this.strings.salePrice : this.strings.regularPrice;
    }

    this.renderSign(variant);
    this.renderPromo(variant);

    // Availability line and buttons.
    let statusText = this.strings.inStock || 'In stock';
    if (!variant) statusText = this.strings.unavailable || 'Unavailable';
    else if (!available) statusText = this.strings.soldOut || 'Sold out';
    if (this.availability) this.availability.classList.toggle('is-available', available);
    if (this.availabilityText) this.availabilityText.textContent = statusText;

    this.renderButton(variant);
    this.renderBagsNote(variant);

    // Gallery: the variant slot shows only the selected variant's photo.
    // Swap it in from its <template> and bring that slide into view.
    const slot = this.gallery?.querySelector('[data-variant-slot]');
    const media = variant && this.section.querySelector(`template[data-variant-media="${variant.id}"]`);
    if (slot && media) {
      slot.replaceChildren(media.content.cloneNode(true));
      const thumb = this.gallery.querySelector('[data-variant-thumb]');
      const thumbMedia = this.section.querySelector(`template[data-variant-thumb-media="${variant.id}"]`);
      if (thumb && thumbMedia) thumb.replaceChildren(thumbMedia.content.cloneNode(true));
      this.gallery.goTo(Number(slot.dataset.index) || 0, { instant: instantGallery });
    }

    if (updateUrl && variant) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch {
        // Some sandboxed previews refuse history changes; the page still works.
      }
    }
  }

  /**
   * "2 bags for $85: $42.50 a bag, $15 less than buying them one at a time"
   * under an option named "Bags", against the same size bought singly.
   */
  renderBagsNote(variant) {
    const note = this.section.querySelector('[data-bags-note]');
    if (!note || !variant) return;
    const key = note.dataset.optionKey;
    const count = parseInt(variant[key], 10) || 0;
    const single = count > 1 && (this.product.variants || []).find((v) => v[key] === '1 bag'
      && ['option1', 'option2', 'option3'].every((k) => k === key || v[k] === variant[k]));
    const saving = single ? single.price * count - variant.price : 0;
    if (!single || saving <= 0 || !this.strings.bagsNote) {
      note.hidden = true;
      return;
    }
    note.textContent = this.strings.bagsNote
      .split('[count]').join(String(count))
      .split('[total]').join(formatMoneyShort(variant.price))
      .split('[each]').join(formatMoneyShort(Math.floor(variant.price / count)))
      .split('[saving]').join(formatMoneyShort(saving));
    note.hidden = false;
  }

  endOffer() {
    if (this.offerEnded) return;
    this.offerEnded = true;
    if (this.offerCard) this.offerCard.hidden = true;
    this.querySelector('[data-addon-included]')?.remove();
    if (this.gift) this.gift.disabled = true;
    const title = this.querySelector('[data-addon-title]');
    if (title) title.textContent = title.dataset.addonTitle;
    else this.querySelector('.product-addon')?.setAttribute('hidden', '');
  }

  /** Add to cart label with the price, plus the add-on's while it is ticked. */
  renderButton(variant) {
    const available = Boolean(variant && variant.available);
    const addonPrice = this.addon?.checked ? Number(this.addon.dataset.addonPrice) || 0 : 0;
    const addLabel = this.strings.addToCartPrice
      ? this.strings.addToCartPrice.split('[price]').join(formatMoneyShort((variant?.price || 0) + addonPrice))
      : (this.strings.addToCart || 'Add to cart');
    const buttonText = available ? addLabel : (variant ? this.strings.soldOut : this.strings.unavailable);
    if (this.button) this.button.disabled = !available;
    if (this.buttonLabel) this.buttonLabel.textContent = buttonText;
    this.sticky?.update(variant, buttonText, available);
  }

  showError(message) {
    if (!this.error) return;
    if (this.errorText) this.errorText.textContent = message;
    this.error.hidden = false;
  }

  hideError() {
    if (this.error) this.error.hidden = true;
  }

  async submit(trigger) {
    const button = this.button;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const cart = window.DoverfieldCart;
    const variant = this.variant;
    if (!cart || !variant) {
      this.showError(this.strings.addError);
      return;
    }
    const quantity = Math.max(1, Math.min(99, Math.floor(Number(this.quantityInput?.value)) || 1));
    this.hideError();
    button.setAttribute('aria-busy', 'true');
    this.sticky?.setBusy(true);
    const started = performance.now();
    try {
      // One add-on covers the order whatever the quantity (one pump does every
      // cube). While it's free it goes in as its own line, once per cart, with
      // a Free gift property and a private _gift one; a ticked box adds a paid
      // one. The free line goes in last so it lists first in the cart.
      const items = [{ id: variant.id, quantity }];
      if (this.addon?.checked) items.push({ id: Number(this.addon.value), quantity: 1 });
      if (this.gift && !this.gift.disabled) {
        const current = await cart.get();
        if (!(current?.items || []).some((item) => item.gift)) {
          items.push({
            id: Number(this.gift.dataset.addonId),
            quantity: 1,
            properties: { [this.gift.dataset.giftKey]: this.gift.value, _gift: '1' },
          });
        }
      }
      await cart.add(items.length > 1 ? { items } : items[0]);
      // Keep the spinner up long enough to read as feedback (at most 400ms).
      const elapsed = performance.now() - started;
      if (elapsed < 240 && !prefersReducedMotion()) await wait(240 - elapsed);
      if (this.live) {
        this.live.textContent = (this.strings.addedLive || '').replace('[title]', this.product.title);
      }
      document.dispatchEvent(new CustomEvent('cart:open', {
        cancelable: true,
        detail: { trigger: trigger || button, source: 'product-form' },
      }));
    } catch (error) {
      const message = error && error.status === 422 ? this.strings.soldOutError : this.strings.addError;
      this.showError(message || (error && error.description) || '');
    } finally {
      button.removeAttribute('aria-busy');
      this.sticky?.setBusy(false);
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Sticky add-to-cart bar (mobile)                                           */
/* ------------------------------------------------------------------------ */

class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('section') || document;
    this.form = this.section.querySelector('product-form');
    this.button = this.querySelector('[data-sticky-add]');
    this.label = this.querySelector('[data-sticky-label]');
    this.price = this.querySelector('[data-sticky-price]');
    this.was = this.querySelector('[data-sticky-was]');
    this.wasAmount = this.querySelector('[data-sticky-was-amount]');
    this.picker = this.section.querySelector('variant-picker');
    const target = this.section.querySelector('[data-add-to-cart]');
    if (!target || !this.button) return;
    this.mode = 'hidden';
    this.addText = this.label?.textContent.trim() || '';
    this.available = !this.button.disabled;

    this.button.addEventListener('click', () => {
      if (this.mode === 'choose') this.scrollToPicker();
      else this.form?.submit(this.button);
    });

    // Three states, from where the picker and the main button sit:
    // both below the screen -> "Choose your pack" (scrolls to the picker);
    // either on screen -> hidden; the button above the screen -> Add to cart.
    this.onScreen = new Map();
    const watched = [this.picker, target].filter(Boolean);
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => this.onScreen.set(entry.target, entry));
      this.refresh(target);
    });
    watched.forEach((el) => this.observer.observe(el));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  refresh(target) {
    const entries = Array.from(this.onScreen.values());
    if (entries.some((entry) => entry.isIntersecting)) {
      this.setMode('hidden');
      return;
    }
    const buttonEntry = this.onScreen.get(target);
    const passed = buttonEntry && buttonEntry.boundingClientRect.top < 0;
    this.setMode(passed ? 'add' : 'choose');
  }

  setMode(mode) {
    this.mode = mode;
    const visible = mode !== 'hidden';
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    if (this.button) {
      this.button.tabIndex = visible ? 0 : -1;
      this.button.disabled = mode === 'add' && !this.available;
    }
    if (this.label) {
      this.label.textContent = mode === 'choose' ? (this.button?.dataset.chooseLabel || this.addText) : this.addText;
    }
  }

  scrollToPicker() {
    const target = this.picker || this.form;
    if (!target) return;
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
    const first = target.querySelector('[data-option-input]:checked') || target.querySelector('[data-option-input]');
    first?.focus({ preventScroll: true });
  }

  update(variant, text, available) {
    if (this.price && variant) this.price.textContent = formatMoney(variant.price);
    // On sale, the regular (compare-at) price crossed out after it.
    if (this.was && variant) {
      const compare = Number(variant.compare_at_price) || 0;
      this.was.hidden = compare <= variant.price;
      if (this.wasAmount) this.wasAmount.textContent = compare > variant.price ? formatMoney(compare) : '';
    }
    this.addText = text;
    this.available = available;
    this.setMode(this.mode);
  }

  setBusy(busy) {
    if (!this.button) return;
    if (busy) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
  }
}

/* ------------------------------------------------------------------------ */
/* Product video (gallery slide)                                             */
/* ------------------------------------------------------------------------ */

/**
 * <product-video>: a muted, looping clip that plays only while on screen
 * and never on its own with reduced motion (then it waits for the button).
 */
class ProductVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-video-toggle]');
    if (!this.video) return;
    this.strings = {
      play: this.button?.getAttribute('aria-label') || 'Play video',
      pause: this.button?.dataset.pauseLabel || 'Pause video',
    };
    this.userPaused = prefersReducedMotion();
    this.button?.addEventListener('click', () => {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
      this.sync();
    });
    this.video.addEventListener('play', () => this.sync());
    this.video.addEventListener('pause', () => this.sync());
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.userPaused) this.play();
      else if (!entry.isIntersecting) this.video.pause();
    }, { threshold: 0.6 });
    this.observer.observe(this);
    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  play() {
    this.video.play().catch(() => this.sync());
  }

  sync() {
    const playing = !this.video.paused;
    this.classList.toggle('is-playing', playing);
    this.button?.setAttribute('aria-label', playing ? this.strings.pause : this.strings.play);
  }
}

defineElement('product-gallery', ProductGallery);
defineElement('variant-picker', VariantPicker);
defineElement('sticky-atc', StickyAtc);
defineElement('product-video', ProductVideo);
defineElement('product-form', ProductForm);
